import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mandantenAPI, reportsAPI } from '../lib/api';

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n === 0) return '';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(n);
}

// Default to current month
function currentMonthRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(lastDay) };
}

export default function Susa() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const init = currentMonthRange();
  const [mandant, setMandant] = useState(null);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [includeVorschlag, setIncludeVorschlag] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    return reportsAPI
      .susa(slug, { from, to, include_vorgeschlagen: includeVorschlag })
      .then((r) => setItems(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'SuSa konnte nicht geladen werden')
      )
      .finally(() => setLoading(false));
  }, [slug, from, to, includeVorschlag]);

  useEffect(() => { reload(); }, [reload]);

  const totals = useMemo(() => {
    let soll = 0, haben = 0, anz = 0;
    for (const r of items) {
      soll += Number(r.soll_summe);
      haben += Number(r.haben_summe);
      anz += r.buchungen_anzahl;
    }
    return { soll, haben, anz, diff: soll - haben };
  }, [items]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Summen- und Saldenliste</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <Link to={`/mandanten/${slug}/reports/opl`} className="btn-text">
            Offene Posten →
          </Link>
          <Link to={`/mandanten/${slug}/reports/ustva`} className="btn-text">
            UStVA →
          </Link>
        </div>
      </header>

      <div className="filters" style={{ flexWrap: 'wrap' }}>
        <label>
          Von: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Bis: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeVorschlag}
            onChange={(e) => setIncludeVorschlag(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Vorschläge einbeziehen
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : items.length === 0 ? (
        <div className="card empty">Keine Buchungen im Zeitraum.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Konto</th>
              <th>Soll-Summe</th>
              <th>Haben-Summe</th>
              <th>Saldo Soll</th>
              <th>Saldo Haben</th>
              <th>Anz.</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const saldoNum = Number(r.saldo);
              return (
                <tr
                  key={r.konto}
                  className="row-link"
                  onClick={() => navigate(`/mandanten/${slug}/reports/konto/${r.konto}?from=${from}&to=${to}${includeVorschlag ? '&include_vorgeschlagen=true' : ''}`)}
                >
                  <td><b>{r.konto}</b></td>
                  <td className="num">{fmtMoney(r.soll_summe)}</td>
                  <td className="num">{fmtMoney(r.haben_summe)}</td>
                  <td className="num">{saldoNum > 0 ? fmtMoney(saldoNum) : ''}</td>
                  <td className="num">{saldoNum < 0 ? fmtMoney(-saldoNum) : ''}</td>
                  <td className="num muted">{r.buchungen_anzahl}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 600 }}>
              <td>Summe</td>
              <td className="num">{fmtMoney(totals.soll)}</td>
              <td className="num">{fmtMoney(totals.haben)}</td>
              <td colSpan={2} className="num muted">
                {Math.abs(totals.diff) < 0.005
                  ? 'Ausgeglichen ✓'
                  : `Differenz ${fmtMoney(totals.diff)}`}
              </td>
              <td className="num muted">{totals.anz}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
